/**
 * Who each pilot is after this tick — the target-selection half of the
 * roster's decision pass, split from `encounter-tick.ts` so the tick file
 * stays the pipeline and this stays the policy.
 */

import type { CombatAircraft } from "./encounter.ts";
import { bodyOf, type CombatBody } from "./geometry.ts";
import { targetBody, type EncounterTarget } from "./targets.ts";

/** Everything on the other side that is still worth shooting at or running from. */
export function opponentsOf(roster: readonly CombatAircraft[], entry: CombatAircraft): readonly CombatAircraft[] {
  return roster.filter((other) => other.side !== entry.side && other.status !== "destroyed");
}

/** The nearest live opponent — what a single-seat pilot is fighting. */
export function primaryOpponent(
  roster: readonly CombatAircraft[],
  entry: CombatAircraft,
): CombatAircraft | null {
  let best: CombatAircraft | null = null;
  let bestRange = Number.POSITIVE_INFINITY;
  for (const other of opponentsOf(roster, entry)) {
    const dx = other.sim.position.x - entry.sim.position.x;
    const dy = other.sim.position.y - entry.sim.position.y;
    const dz = other.sim.position.z - entry.sim.position.z;
    const range = Math.hypot(dx, dy, dz);
    if (range < bestRange) {
      best = other;
      bestRange = range;
    }
  }
  return best;
}

/** The first target on this flight's strike list that is still worth attacking. */
export function assignedTargetOf(targets: readonly EncounterTarget[], entry: CombatAircraft): EncounterTarget | null {
  for (const id of entry.strikeTargetIds) {
    const target = targets.find((candidate) => candidate.id === id);
    if (target && !target.destroyed && !target.burning && target.side !== entry.side) return target;
  }
  return null;
}

export interface Quarry {
  readonly id: string;
  readonly body: CombatBody;
}

function rangeBetween(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): number {
  return Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
}

/**
 * What this pilot is after. For everyone without a strike assignment this is
 * exactly what it has always been — the nearest live opposing aeroplane — and
 * the recorded `quarryId` changes nothing about how they fly.
 *
 * A flight that *was* sent after a balloon has a choice to make every tick, and
 * it is made with the same asymmetric pair of ranges the route AI uses: an
 * aeroplane inside `aiEngageRangeM` takes precedence over the gasbag, and once
 * it is the quarry it stays the quarry until it is clear of the wider
 * `aiDisengageRangeM`. One threshold instead of two would leave a burner with a
 * defender circling at exactly the engage range swapping quarry every tick,
 * flying half of each attack and completing neither.
 */
export function pickQuarry(
  aircraft: readonly CombatAircraft[],
  targets: readonly EncounterTarget[],
  entry: CombatAircraft,
): Quarry | null {
  const opponent = primaryOpponent(aircraft, entry);
  const asAircraft: Quarry | null = opponent ? { id: opponent.id, body: bodyOf(opponent.sim) } : null;
  if (entry.strikeTargetIds.length === 0) return asAircraft;
  const assigned = assignedTargetOf(targets, entry);
  if (!assigned) return asAircraft;
  const strike: Quarry = { id: assigned.id, body: targetBody(assigned) };
  if (!opponent || !asAircraft) return strike;
  const tunables = entry.sim.config.tunables;
  const range = rangeBetween(entry.sim.position, opponent.sim.position);
  const limit = entry.quarryId === opponent.id ? tunables.aiDisengageRangeM : tunables.aiEngageRangeM;
  return range <= limit ? asAircraft : strike;
}
